import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _descriptor2, _ProxyRPCService;
import { NestedError } from "abweb/dto/exception/NestedError";
import { ProxyType } from "abweb/dto/generated/proxy/ProxyType";
import { ClientSessionConfiguration } from "abweb/dto/generated/session/ClientSessionConfiguration";
import { AbWebServices } from "abweb/service/AbWebServices";
import { LocaleManager } from "abweb/service/locale/LocaleManager";
import { MethodType } from "abweb/service/rpc/MethodType";
import { PollResponseHandler } from "abweb/service/rpc/PollResponseHandler";
import { RPCPollingService } from "abweb/service/rpc/RPCPollingService";
import { ASYNC_QUEUE_VIEW_FULL, ASYNC_QUEUE_VIEW_UPDATE } from "abweb/service/rpc/StreamingRPCService";
import { TaskPoller } from "abweb/service/rpc/TaskPoller";
import { ViewSubscription } from "abweb/service/rpc/ViewSubscription";
import { ClientSessionService } from "abweb/service/session/ClientSessionService";
import { FetchUtils } from "abweb/service/utils/rpc/FetchUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { DeferredValuePromise } from "abweb/utils/PromiseUtils";
import { TestState } from "abweb/utils/test/TestState";
import { URLUtils } from "abweb/utils/URLUtils";
import { action, computed, makeObservable, observable } from "mobx";
const LOG = LogUtils.getLogger(__filename);
// quick hack to poll through a proxy, really need to do this on server
export let ProxyRPCService = (_class = (_ProxyRPCService = class ProxyRPCService {
  constructor(rpcService, proxyRoot, proxyType = ProxyType.EXTERNAL, direct = false) {
    _initializerDefineProperty(this, "_directProxyClientSessionId", _descriptor, this);
    _defineProperty(this, "rpcService", void 0);
    _defineProperty(this, "proxyRoot", void 0);
    _defineProperty(this, "proxyType", void 0);
    _initializerDefineProperty(this, "taskPoller", _descriptor2, this);
    // Instance Level
    _defineProperty(this, "direct", void 0);
    _defineProperty(this, "directProxySessionInitialized", null);
    if (direct && proxyType === ProxyType.EXTERNAL) {
      throw new Error("Direct proxies must support async apis (polling)");
    }
    this.rpcService = rpcService;
    this.proxyRoot = proxyRoot;
    this.proxyType = proxyType;
    this.direct = direct;
    if (!direct) {
      // If non-direct, directProxySessionInitialized is a no-op, so simply set with a resolved promise.
      this.directProxySessionInitialized = Promise.resolve();

      /*
       * If we are proxying to another Ab Initio web application,
       * we want to set up another task poller to run the long polling through the proxy.
       * If we are proxying to a continuous flows graph service,
       * we want to invoke async calls to the base rpc service, as it knows how to translate async between appserver
       * and graph.
       * Note that we do NOT create TaskPoller for direct proxies here as will be lazily created
       * by first 'init' invocation below
       */
      if (proxyType !== ProxyType.EXTERNAL) {
        // direct proxies lazily create taskPoller in first 'init' invocation below: new TaskPoller(new RPCPollingService(this), -1, -1, true)
        this.taskPoller = new TaskPoller(new RPCPollingService(this), 0, 0, true);
      }
    }
    makeObservable(this);
  }
  get isDirect() {
    return this.direct;
  }
  get directProxyClientSessionId() {
    return this._directProxyClientSessionId;
  }
  setDirectProxyClientSessionId(directProxyClientSessionId) {
    this._directProxyClientSessionId = directProxyClientSessionId;
  }
  get outstandingRequestCounter() {
    return this.rpcService.outstandingRequestCounter;
  }
  get clientSessionId() {
    return this.direct ? this._directProxyClientSessionId // observable
    : this.rpcService.clientSessionId; // observable
  }
  static registerRPCServices(applicationType, proxyServiceInitializer, allowDirect = true) {
    ProxyRPCService.registerProxyRPCServices(applicationType.toString(), serviceInfo => serviceInfo.type === applicationType, proxyServiceInitializer, allowDirect);
  }
  static getProxyServiceIdentifier(proxyLabel) {
    let serviceIdentifier = ProxyRPCService.proxyServiceIdentifiers.get(proxyLabel);
    if (!serviceIdentifier) {
      const proxyConfiguration = AbWebServices.clientSessionService.proxyConfiguration;
      if (!proxyConfiguration) {
        throw new Error(`Proxy configuration not available in ProxyRPCServices`);
      }
      const proxyServiceInfo = proxyConfiguration.proxyServiceInfo.find(serviceInfo => serviceInfo.label === proxyLabel);
      if (!proxyServiceInfo) {
        throw new Error(`Unable to find proxy server for label ${proxyLabel}`);
      }
      serviceIdentifier = {
        id: proxyServiceInfo.label,
        displayName: proxyServiceInfo.name,
        type: proxyServiceInfo.type
      };
      ProxyRPCService.proxyServiceIdentifiers.set(proxyServiceInfo.label, serviceIdentifier);
    }
    return serviceIdentifier;
  }
  static registerRPCServiceIdentifiers(applicationType, proxyServiceInitializer, allowDirect = true) {
    ProxyRPCService.registerProxyRPCServices(applicationType.toString(), serviceInfo => serviceInfo.type === applicationType, (rpcService, proxyServiceInfo) => {
      proxyServiceInitializer(rpcService, ProxyRPCService.getProxyServiceIdentifier(proxyServiceInfo.label), proxyServiceInfo.isDefault);
    }, allowDirect);
  }
  static registerExternalRPCServices(apiName, proxyServiceInitializer) {
    ProxyRPCService.registerProxyRPCServices(apiName, serviceInfo => serviceInfo.type === ProxyType.EXTERNAL && serviceInfo.apiName === apiName, proxyServiceInitializer);
  }
  static registerProxyRPCServices(api, filter, proxyServiceInitializer, allowDirect = true) {
    let rpcService = AbWebServices.getRPCService();
    let proxyConfiguration = AbWebServices.clientSessionService.proxyConfiguration;
    if (proxyConfiguration) {
      proxyConfiguration.proxyServiceInfo.filter(filter).map(serviceInfo => {
        const direct = serviceInfo.isDirect === true && allowDirect;
        let proxyRPCService = ProxyRPCService.proxyServices.get(serviceInfo.label);
        if (proxyRPCService == null) {
          let proxyRoot = direct ? `${serviceInfo.proxyRoot}` : "/api/abweb/proxy" + "/" + serviceInfo.label; //`${serviceInfo.proxyRoot}/${serviceInfo.label}`;
          proxyRPCService = new ProxyRPCService(rpcService, proxyRoot, serviceInfo.type, direct);
          ProxyRPCService.proxyServices.set(serviceInfo.label, proxyRPCService);
        }
        proxyServiceInitializer(proxyRPCService, serviceInfo);
      });
    }
  }
  static getProxyRPCService(proxyLabel) {
    return ProxyRPCService.proxyServices.get(proxyLabel);
  }
  static reset() {
    ProxyRPCService.proxyServices.forEach(proxyRPCService => proxyRPCService.resetPoller());
  }
  resetPoller() {
    if (this.direct) {
      this.closeDirectProxySession();
    } else {
      this.taskPoller = this.taskPoller?.reset();
    }
  }
  closeDirectProxySession() {
    this.directProxyLogout();
    // Will need to recalc new clientSessionId lazily on the new directProxy rpc
    this.taskPoller?.stopPolling();
    this.taskPoller = undefined;
    this.setDirectProxyClientSessionId(null);
    this.directProxySessionInitialized = null;
  }
  directProxyLogout() {
    // ignore returned redirected content response (typically karma 'debug.html' ...)
    return this._directProxyClientSessionId ? this.proxyInvokeWrapper("/authenticate/j_spring_security_logout?useReferer=true", proxyUrl => this.rpcService.invokeVoid(proxyUrl, {}, MethodType.GET)) : Promise.resolve();
  }
  setShuttingDown() {
    this.rpcService.setShuttingDown();
    if (this.direct) {
      this.directProxyLogout(); // Be nice and try to logout of direct proxy session (otherwise have to wait until it times out)
    }
  }
  invokeVoid(appRelativeUrl, namedParams, init, countRequest, autoAuthRetry) {
    return this.proxyInvokeWrapper(appRelativeUrl, proxyUrl => this.rpcService.invokeVoid(proxyUrl, namedParams, init, countRequest, autoAuthRetry));
  }
  invokeRetBlob(appRelativeUrl, namedParams, init, countRequest) {
    return this.proxyInvokeWrapper(appRelativeUrl, proxyUrl => this.rpcService.invokeRetBlob(proxyUrl, namedParams, init, countRequest));
  }
  invokeUploadBlob(appRelativeUrl, toUpload, countRequest) {
    return this.proxyInvokeWrapper(appRelativeUrl, proxyUrl => this.rpcService.invokeUploadBlob(proxyUrl, toUpload, countRequest));
  }

  /**
   * Use directly if returned value is a json primitive (string, number, boolean, or an array or map of the primitives),
   * as these to not need to be converted to corresponding typescript class instances.
   * Can NOT use if your rpc returns void (use invokeVoid instead).
   */
  // Note that all invokeRet* methods funnel through this method (so auth retry logic works for all)
  invokeRetJSON(appRelativeUrl, namedParams, init, countRequest, cacheBustConfig) {
    return this.proxyInvokeWrapper(appRelativeUrl, proxyUrl => this.rpcService.invokeRetJSON(proxyUrl, namedParams, init, countRequest, cacheBustConfig), init, cacheBustConfig);
  }

  /** Helper method to deserialize return value to a given Object */
  invokeRetObj(appRelativeUrl, namedParams, fnCtor, init, countRequest) {
    return this.proxyInvokeWrapper(appRelativeUrl, proxyUrl => this.rpcService.invokeRetObj(proxyUrl, namedParams, fnCtor, init, countRequest));
  }

  /** Helper method to deserialize return value to an array of instances of RetT */
  invokeRetObjArray(appRelativeUrl, namedParams, fnCtor, init, countRequest) {
    return this.proxyInvokeWrapper(appRelativeUrl, proxyUrl => this.rpcService.invokeRetObjArray(proxyUrl, namedParams, fnCtor, init, countRequest));
  }
  invokeRetEnumArray(appRelativeUrl, namedParams, enumType, init) {
    return this.proxyInvokeWrapper(appRelativeUrl, proxyUrl => this.rpcService.invokeRetEnumArray(proxyUrl, namedParams, enumType, init));
  }
  invokeRetEnum(appRelativeUrl, namedParams, enumType, init) {
    return this.proxyInvokeWrapper(appRelativeUrl, proxyUrl => this.rpcService.invokeRetEnum(proxyUrl, namedParams, enumType, init));
  }

  /** Helper method to deserialize return value to an array of instances of RetT */
  invokeRetObjMap(appRelativeUrl, namedParams, fnCtor, init) {
    return this.proxyInvokeWrapper(appRelativeUrl, proxyUrl => this.rpcService.invokeRetObjMap(proxyUrl, namedParams, fnCtor, init));
  }
  invokeRetXml(appRelativeUrl, namedParams, methodType) {
    return this.proxyInvokeWrapper(appRelativeUrl, proxyUrl => this.rpcService.invokeRetXml(proxyUrl, namedParams, methodType));
  }

  ///////   StreamingRPCService Implementation below ////////

  getIsReady() {
    return this.taskPoller ? !this.taskPoller.canceled : this.direct ? true : this.rpcService.getIsReady();
  }
  get taskPollerId() {
    return this.taskPoller ? this.taskPoller.taskPollerId : this.rpcService.taskPollerId;
  }
  invokeAsyncVoid(appRelativeUrl, namedParams, messageHandlers, init) {
    return this.taskPoller ? this.taskPoller.startPollingTaskVoid(appRelativeUrl, namedParams, new PollResponseHandler(null, messageHandlers), init) : this.rpcService.invokeAsyncVoid(this.processProxyURL(appRelativeUrl, init), namedParams, messageHandlers, init);
  }
  invokeAsync(appRelativeUrl, namedParams, pollResponseParser, messageHandlers, init) {
    return this.taskPoller ? this.taskPoller.startPollingTask(appRelativeUrl, namedParams, new PollResponseHandler(pollResponseParser, messageHandlers), init) : this.rpcService.invokeAsync(this.processProxyURL(appRelativeUrl, init), namedParams, pollResponseParser, messageHandlers, init);
  }
  invokeView(appRelativeUrl, namedParams, fullViewHandler, updateViewHandler, init) {
    if (this.taskPoller) {
      let messageHandlers = {};
      messageHandlers[ASYNC_QUEUE_VIEW_FULL] = fullViewHandler;
      messageHandlers[ASYNC_QUEUE_VIEW_UPDATE] = updateViewHandler;
      return new ViewSubscription(this.taskPoller.startPollingTask(appRelativeUrl, namedParams, new PollResponseHandler(fullViewHandler, messageHandlers, true), init));
    } else {
      return this.rpcService.invokeView(this.processProxyURL(appRelativeUrl, init), namedParams, fullViewHandler, updateViewHandler, init);
    }
  }
  setClientSessionId(clientSessionId, pollConfiguration) {
    // Make linter happy
    // normal proxies use underlying rpcService client session,
    // and direct proxies use clientSessionId established by createDirectProxyClientSession() on lazy init() calls,
    // so will NEVER be external callers of setClientSessionId on a ProxyRPCService instance that matter
  }
  closeClientSession() {
    //make linter happy
  }
  makeGetUrl(relativeUrl) {
    return this.rpcService.makeGetUrl(this.processProxyURL(relativeUrl));
  }
  makeAbsoluteGetUrl(appRelativeUrl, namedParams = null) {
    return this.rpcService.makeAbsoluteGetUrl(this.processProxyURL(appRelativeUrl), namedParams);
  }

  /**
   * @inheritdoc
   */
  openDownloadLink(appRelativeUrl, namedParams, untilComplete) {
    // Can't use proxyInvokeWrapper as openDownloadLink does not return a promise.
    this.init().then(() => {
      this.rpcService.openDownloadLink(this.processProxyURL(appRelativeUrl), namedParams, untilComplete);
    }).catch(err => {
      throw new NestedError("Error while invoking proxiedRPCService openDownlaodLink", err);
    });
  }
  async proxyInvokeWrapper(appRelativeUrl, proxyInvokeFn, reqInit, cacheBustConfig) {
    await this.init();
    const proxyUrl = this.processProxyURL(appRelativeUrl, reqInit, cacheBustConfig);
    return proxyInvokeFn(proxyUrl);
  }
  async init() {
    //Note:  When !this.direct, the constructor sets directProxySessionInitialized to an already resolved promise.
    if (!this.directProxySessionInitialized) {
      // Set unresolved promise BEFORE any awaits to ensure only one entrant into this !this.directProxySessionInitialized block
      const deferredP = new DeferredValuePromise();
      this.directProxySessionInitialized = deferredP.awaitValue();
      let clientSessionConfiguration;
      try {
        // Throws server-side exception if can obtain directProxyToken
        clientSessionConfiguration = await this.createDirectProxyClientSession();
      } catch (err) {
        // The user at the very least will need to re-login to fix the rpcs t o the directProxy target, but it is
        // also likely that the server is misconfigured (ag not being used, config value for directProxy not set, ...)
        // so they are probably wedged until an admin fixes the server-side configuration.
        deferredP.rejectIt(new NestedError("Unable to create a direct proxy session for proxyRoot: " + this.proxyRoot, err));
        return this.directProxySessionInitialized;
      }
      this.setDirectProxyClientSessionId(clientSessionConfiguration.id);

      // Most users won't view data (invoke direct proxy rpc), so don't start polling unless first directProxy rpc.
      // Lazily start the task poller to keep the new direct proxy client session alive forevermore.
      // The args <-1, -1> means start polling now, and never stop

      // Note: There is a race condition between  the proxied rpcs and the TaskPoller fully initializing, as we
      // don't 'await' the TaskPoller async requests for establishing polling infrastructure (constructor api doesn't allow it).
      // However, it is ok to call polling rpcs before TaskPoller fully initialized as the polling rpc results will
      // just queue on the server, and be returned later when the polling infrastructure is initialized (per Elliot)
      this.taskPoller = new TaskPoller(new RPCPollingService(this), -1, -1, clientSessionConfiguration.pollConfiguration.isWSDisabled, this, this.proxyRoot);
      deferredP.resolveIt(); // release the hounds!
    }
    return this.directProxySessionInitialized;
  }
  async createDirectProxyClientSession() {
    // defensive
    if (this._directProxyClientSessionId) {
      throw new Error("Can not invoke createDirectProxyClientSession if directProxyClientSessionId is already set.");
    }

    // This used to be more complicated pre-PORTAL-1151. Go through Perforce history if curious.
    await this.initDirectSessionLogin();

    // Setting the ai_locale req param will cause the AbInitioLocaleFilter to set the ai_locale cookie accordingly
    // (ignores any existing ai_locale cookie)

    // delete ai_locale_preferred cookie as will be ignored in favor of ai_locale req param (not strictly needed)
    // proxyRoot is something like '/../datacatalog', which is interpreted relative to current context root.
    // The resolved hostPath should be contextRoot of proxy (e.g., "/datacatalog" or "/myApps/datacatalog")
    const hostPath = URLUtils.getAbsolutePath(this.proxyRoot);
    !TestState.TEST_MODE && URLUtils.deleteCookie(LocaleManager.PREFERRED_LOCALE_COOKIE_NAME, hostPath);

    // Below ai_locale req param causes existing ai_locale cookie to be updated, so no need to delete it
    const clientSessionInitParams = {};
    clientSessionInitParams[ClientSessionService.XX_FILE_BROWSER] = "true";
    clientSessionInitParams["localeVal"] = LocaleManager.languageLocaleParam;
    const configuration = await this.rpcService.invokeRetObj(this.processProxyURL(`/api/abweb/session/init?${LocaleManager.LOCALE_REQUEST_PARAM}={localeVal}`), clientSessionInitParams, ClientSessionConfiguration, FetchUtils.buildFormRequest());
    // checkDirectProxyLocale(hostPath);  // Not currently possible, see below
    return configuration;
  }

  /*
    private checkDirectProxyLocale(hostPath: string): void {
      // Sigh: cookie api does not support reading 'external' cookies on the same domain
      // (other than gross iframe hack that requires fetching something from direct proxy)
      // so can not check the actual resolved ai_locale cookie value.  So no warning for now...
  
      // Implementation Idea: invoke above 'session init' rpc via iframe and access the resulting
      // iframe-accessible ai_locale cookie, but would need to verify that both the resulting
      // clientId and ai_locale direct proxy cookie are valid in non-iframe app space
      const appLocale = LocaleManager.languageLocale;
      const directProxyLocale = URLUtils.readCookie(LocaleManager.LOCALE_COOKIE_NAME, hostPath);
      if (directProxyLocale !== appLocale) {
        LOG.warn(`Direct proxy '${hostPath}' has locale=${directProxyLocale}, which does not match the application locale=${appLocale}.`);
      }
    }
  */

  async initDirectSessionLogin() {
    // Post a request to return the ag token.  It will throw if server can't get a token for the current user.
    const agSessionTokenFetch = this.rpcService.invokeRetJSON("/api/abweb/session/directProxyToken", null);
    const mHubCheck = this.rpcService.invokeRetJSON(this.processProxyURL("/api/abweb/product/productInfo"), null);

    // we want these calls to run on parallel, but we prioritize errors from AG
    const results = await Promise.allSettled([agSessionTokenFetch, mHubCheck]);
    if (results[0].status === "rejected") {
      throw results[0].reason;
    }
    const agSessionToken = results[0].value;
    if (results[1].status === "rejected") {
      throw results[1].reason;
    }

    // Note: as we are setting up the client session, can NOT use the proxiedRPCService invoke* methods as they
    // depend on (await) the promise returned by the 'init' function that calls this function.
    // So instead, handcraft the rpcs that are invoked against the direct proxied application.
    const reqInit = FetchUtils.buildFormRequest("*/*");
    reqInit.redirect = "follow";
    const namedParams = {
      optional: true,
      j_username: AbWebServices.sessionData.currentUser.username,
      j_password: agSessionToken
    };
    await this.rpcService.invokeVoid(this.processProxyURL("/authenticate/j_spring_security_check"), namedParams, reqInit, true, false);
  }
  processProxyURL(url, reqInit, cacheBustConfig) {
    const doCache = typeof reqInit === "number" ? false : FetchUtils.isCachingEnabled(reqInit?.cache);
    if (this.clientSessionId && this.direct && !doCache && !cacheBustConfig) {
      url = URLUtils.appendQueryParamsToUrl(url, {
        [ClientSessionService.CLIENT_SESSION_ID]: this.clientSessionId
      });
    }
    return this.proxyRoot + url;
  }

  /*
    public async createIFrameBasedDirectProxyClientSession() {
           const form: HTMLFormElement = document.createElement("form");
           form.setAttribute("target", "iframe-" + "proxylabel");
           form.setAttribute("method", "post");
           form.setAttribute("action", this.proxyRoot + "/authenticate/j_spring_security_check");
           const optional = form.appendChild(document.createElement("input"));
           optional.setAttribute("type", "text");
           optional.setAttribute("name", "optional");
           optional.setAttribute("value", "true");
           const username = form.appendChild(document.createElement("input"));
           username.setAttribute("type", "text");
           username.setAttribute("name", "j_username");
           username.setAttribute("value", AbWebServices.sessionData.currentUser.username);
           const password = form.appendChild(document.createElement("input"));
           password.setAttribute("type", "text");
           password.setAttribute("name", "j_password");
           password.setAttribute("value", token);
           const successURL = form.appendChild(document.createElement("input"));
           successURL.setAttribute("type", "text");
           successURL.setAttribute("name", "success_url");
           successURL.setAttribute("value", this.proxyRoot + "/");
           form.appendChild(optional);
           form.appendChild(username);
           form.appendChild(password);
           form.appendChild(successURL);
            const iframe: HTMLIFrameElement = document.createElement("iframe");
           iframe.setAttribute("id", "iframe-" + "proxylabel");
           iframe.setAttribute("name", "iframe-" + "proxylabel");
           iframe.style.display = "none";
           iframe.onload = async () => {
             const inner = (document.getElementById("iframe-" + "proxylabel") as any).contentWindow.document as HTMLDocument;
              let url = URLUtils.addParamsToUrl(this.proxyRoot + "/api/service/csrfToken", {});
             await this.rpcService.invokeVoid(url, null, MethodType.POST);
             this.csrfToken = inner.cookie;
             const ca: string[] = inner.cookie.split(";");
             for (let c of ca)
             {
               while (c.charAt(0) === " ")
               {
                 c = c.substring(1, c.length);
               }
               if (c.indexOf("csrfToken") === 0)
               {
                 this.csrfToken = c.substring("csrfToken".length, c.length);
               }
             }
               url = URLUtils.addParamsToUrl(this.proxyRoot + "/api/abweb/session/init", {"csrfToken": this.csrfToken});
              let url = URLUtils.addParamsToUrl(this.proxyRoot + "/api/abweb/session/init", {});
             const config = await this.rpcService.invokeRetObj<ClientSessionConfiguration>(url, null, ClientSessionConfiguration, MethodType.POST);
             if (config)
             {
               this.setClientSessionId(config!.id, config!.pollConfiguration);
                resolve();
                setTimeout(() => {
                 // Take care to remove iframe after file has been fully downloaded
                 // Josh says: Firefox gets mad if I remove the iframe right away, but it is OK after a little bit
                 form.remove();
                 iframe.remove();
               }, 100);
             }
           }
            const containingNode: HTMLElement = document.getElementById("appGoesHere")!;
           containingNode.appendChild(form);
           containingNode.appendChild(iframe);
           form.submit();
    }
  */
}, _defineProperty(_ProxyRPCService, "proxyServices", new Map()), _defineProperty(_ProxyRPCService, "proxyServiceIdentifiers", new Map()), _ProxyRPCService), _descriptor = _applyDecoratedDescriptor(_class.prototype, "_directProxyClientSessionId", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "taskPoller", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "setDirectProxyClientSessionId", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setDirectProxyClientSessionId"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "taskPollerId", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "taskPollerId"), _class.prototype), _class);
//# sourceMappingURL=ProxyRPCService.js.map
